import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Admin from "../Admin/Admin.jsx";
import { hasAccess } from "../../utils/permissions.js";
import { transformLevelContent } from "../../utils/transformLevelContent.js";
import { mergedContent } from "../../utils/mergeContentByLevel.js";
import { slugify } from "../../utils/slugify.js";
import { videoTitle, docTitle, utilsTitle } from "../../utils/titles.js";
import { useTranslation } from "../../utils/hooks/translateHook.js";
import { useLevelContentData } from "../../utils/hooks/translateLevelContentDataHook.js";
import fundamentals from "./config.js";
import { courseRegistry } from "../../courses/courseRegistry.js";
import "../ContentHtml/Content.scss";
import favicon from "../../images/icons/fun-icon-courses.svg";
import arrowRight from "../../images/icons/arrowRight.svg";
import placeholderMain from "../../images/content-F.jpg";
import placeholderSide from "../../images/content-f-2.jpg";

const extractPrefix = (value) => {
  const str = typeof value === "string" ? value : value?.stableKey || value?.title || "";
  const match = str.match(/^(\d+)[.-](\d+)[.-](\d+)/);
  return match ? `${match[1]}.${match[2]}.${match[3]}` : null;
};

const buildVideoPrefixMap = (levels = []) => {
  const map = {};
  levels.forEach((level) => {
    (level.subjects || []).forEach((subject) => {
      (subject.videos || []).forEach((video) => {
        const prefix = extractPrefix(video);
        if (prefix) { map[prefix] = video; }
      });
    });
    (level.videos || []).forEach((group) => {
      (group.list || []).forEach((video) => {
        const prefix = extractPrefix(video);
        if (prefix) { map[prefix] = video; }
      });
    });
  });
  return map;
};

const LevelContent = ({user, setCurrentUser, isAdminPreview = false,}) => {
  const { courseSlug } = useParams();
  const courseConfig = courseRegistry[courseSlug];
  const navigate = useNavigate();
  const t = useTranslation();
  const [selectedSubjectIdx, setSelectedSubjectIdx] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const translatedData = useLevelContentData(fundamentals.levelContentData, fundamentals.slug) || [];
  const transformedData = useMemo(() => { return transformLevelContent(translatedData); }, [translatedData]);

  const englishVideoByPrefix = useMemo(() => {
    return buildVideoPrefixMap(courseConfig?.levelContentData || [] );
  }, [courseConfig]);

  const allMerged = useMemo(() => { return mergedContent(transformedData); }, [transformedData]);

  if (user?.subscriptionLevel === 9999 && !isAdminPreview) { return <Admin />;}

  const userLevelContent = transformedData
   .slice().sort((a, b) => b.level - a.level).find((item) => hasAccess(user, item.level, courseSlug));

  const mergedForSelected = userLevelContent
    ? allMerged.find( (item) => item.level === userLevelContent.level ) : null;

  const levelData = mergedForSelected?.subjects?.map((subject) => subject.title) || [];
  
  const selectedSubject =
    selectedSubjectIdx !== null
      ? mergedForSelected?.subjects?.[selectedSubjectIdx]
      : null;

  const handleSubjectSelection = (idx) => { setSelectedSubjectIdx(idx); setSelectedType(null); };

  return (
    <div className="course-page course-fundamental">
      <div className="content-section">
        <div className="white-frame">
          <div className="white-frame__content">
            <div className="location-fav">
              <img className="fav-icon" src= {favicon} alt=""/>
              <span className="location-separator">|</span>
              <span className="location-text">
                {t("courses")}
              </span>
            </div>
            <div className="content-profile">
              <h1>
                {t("level")?.toUpperCase()}{" "}
                <strong> {userLevelContent?.title?.toUpperCase()} </strong> <br/>
                {userLevelContent?.description && (
                  <span className="fundamental-level-description">
                    {userLevelContent.description}
                  </span>
                )}
              </h1>
            </div>

            <div className="content-info">
              <ul className="contentItems-list">
                {levelData.map((item, idx) => (
                  <li
                    key={`${item}-${idx}`}
                    className={`contentItems-item ${selectedSubjectIdx === idx ? "active" : ""}`}
                    onClick={() => handleSubjectSelection(idx)}
                  >
                    <img className="arrow-icon1" src={arrowRight} alt=""/>
                    {item?.toUpperCase()}
                  </li>
                ))}
              </ul>
            </div>

            <div className="map-content-panel">
              {selectedSubjectIdx === null && (
                <div className="center-image">
                  <img src={placeholderMain} alt="Fundamentals course"/>
                </div>
              )}

              {selectedSubject && (
                <div className="subject-content-wrapper">
                  <div className="subject-column">
                    <div className="subject-block">
                      <div className="subject-title1">
                        {selectedSubject.title}
                      </div>
                      <div className="subject-sections">
                        {selectedSubject.videos?.length > 0 && (
                          <div
                            className={`section-link ${
                              selectedType === "Lessons"
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => setSelectedType("Lessons")}
                          >
                            {t("lessons")}
                          </div>
                        )}

                        {selectedSubject.docs?.length > 0 && (
                          <div
                            className={`section-link ${
                              selectedType === "Documents"
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => setSelectedType("Documents")}
                          >
                            {t("ebooks")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="content-column">
                    {!selectedType && (
                      <div className="center-image">
                        <img src={placeholderSide} alt="Choose content type"/>
                      </div>
                    )}

                    {selectedType && (
                      <div className="content-list">
                       {selectedType === "Lessons" &&
                        selectedSubject.videos?.map((video, index) => {
                          const prefix = extractPrefix(video);
                          const englishTitle = englishVideoByPrefix[prefix] || video;
                          const canonicalSlug =
                            slugify( typeof englishTitle === "string"
                              ? englishTitle : englishTitle?.stableKey || englishTitle?.title || "");
                          return (
                            <div className="lecture-item-content"
                              key={`${canonicalSlug}-${index}`}
                            >
                              <Link to={`/course/${courseSlug}/videos/${canonicalSlug}`}>
                                {videoTitle(
                                  typeof video === "string"
                                    ? video : video?.title || video?.stableKey || ""
                                )}
                              </Link>
                            </div>
                          );
                        })}

                        {selectedType === "Documents" &&
                          selectedSubject.docs?.map((doc, index) => (
                            <div
                              key={`${slugify(doc)}-${index}`}
                              className="lecture-item-content"
                            >
                              <Link to={`/course/${courseSlug}/docs/${slugify(doc)}`}>
                                {docTitle(doc)}
                              </Link>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedSubject?.utils?.length > 0 && (
                <details className="fundamental-utilities">
                  <summary className="section-utilsSubheading">
                    {t("utilities")}
                    <span className="dropdown-icon" />
                  </summary>
                  <div className="map-utilities-panel">
                    <ul className="map-utils-list">
                      {selectedSubject.utils.map((filename, index) => (
                        <li key={`${filename}-${index}`}>
                          <Link to={`/utils/${slugify(filename)}`}>
                            {utilsTitle(filename)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelContent;